/* eslint-disable prettier/prettier */
import { useState, useRef, useEffect } from 'react';
import { FolderType } from '../../types/FolderType';
import {
  ChevronDown,
  ChevronRight,
  Folder,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Upload,
  FileText,
} from 'lucide-react';
import { ConfirmDeleteModal } from '../file/ConfirmDeleteModal';
import { FileUploadModal } from '../file/FileUploadModal';
import { FileService } from '../../services/file.service';

export const FolderTree = ({
  folder,
  onEdit,
  onDelete,
  onAdd,
}: {
  folder: FolderType;
  onEdit: (f: FolderType) => void;
  onDelete: (f: FolderType) => void;
  onAdd: (f: FolderType) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sharedRowStyle =
    'flex items-center justify-between px-4 py-3 sm:w-[90%] text-sm text-gray-700 border rounded border-muted hover:bg-gray-50';

  return (
    <div className="relative group">
      <div
        className={sharedRowStyle}
        onDragOver={(e) => e.preventDefault()}
        onDrop={async (e) => {
          const fileId = e.dataTransfer.getData('text/plain');
          if (!fileId) return;

          const targetFolderId =
            folder.id === 'uncategorized' || folder.id === 'uncategorized-virtual'
              ? null
              : folder.id;

          try {
            await FileService.moveFilesToFolder([fileId], targetFolderId);
            window.location.reload();
          } catch (err) {
            console.error('Failed to move file by drag-and-drop', err);
          }
        }}
      >
        <div className="flex items-center w-[85%] gap-1 font-medium text-primary">
          {folder.subfolders?.length || folder.files?.length ? (
            <button onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <Folder className="w-5 h-5 mb-[2px] text-primary" />
          <span className="ml-2 truncate" title={folder.folderName}>
            {folder.folderName}
          </span>
        </div>

        <div className="w-[15%] flex justify-end relative">
          {folder.id !== 'uncategorized' && folder.id !== 'uncategorized-virtual' && (
            <>
              <button ref={buttonRef} onClick={() => setIsOpen((prev) => !prev)}>
                <MoreVertical className="w-5 h-5 text-muted-foreground" />
              </button>
              {isOpen && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 z-10 mt-2 bg-white border rounded shadow-md w-28"
                >
                  <button
                    className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-highlight"
                    onClick={() => {
                      setIsOpen(false);
                      setIsUploadModalOpen(true);
                    }}
                  >
                    <Upload className="w-4 h-4" /> Upload
                  </button>

                  <button
                    className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-highlight"
                    onClick={() => {
                      setIsOpen(false);
                      onEdit(folder);
                    }}
                  >
                    <Pencil className="w-4 h-4" /> Edit
                  </button>

                  <button
                    className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-highlight"
                    onClick={() => {
                      setIsOpen(false);
                      onAdd(folder);
                    }}
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>

                  <button
                    className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-red-600 hover:bg-highlight"
                    onClick={() => {
                      setIsOpen(false);
                      setShowConfirm(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="ml-4 space-y-2">
          {Array.isArray(folder.files) && folder.files.length > 0 && (
            <div className="space-y-0">
              {folder.files?.map((file) => (
                <div
                  key={file.id}
                  className={sharedRowStyle}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', file.id);
                  }}
                >
                  <div
                    className="flex items-center gap-2 cursor-pointer hover:underline"
                    onClick={() => window.open(`/files/${file.id}`, '_blank')}
                  >
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="truncate">{file.editedName || file.fileName}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {folder.subfolders?.map((child) => (
            <FolderTree
              key={child.id}
              folder={child}
              onEdit={onEdit}
              onDelete={onDelete}
              onAdd={onAdd}
            />
          ))}

          {folder.subfolders?.length === 0 && (!folder.files || folder.files.length === 0) && (
            <p className="py-2 ml-8 text-sm text-muted-foreground">No content</p>
          )}
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={showConfirm}
        names={[folder.folderName]}
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => {
          onDelete(folder);
          setShowConfirm(false);
        }}
      />

      <FileUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={() => setIsUploadModalOpen(false)}
        forcedFolderId={folder.id}
      />
    </div>
  );
};
